// ImageModal.tsx
'use client';

// npm library imports
import { useEffect, useRef } from 'react';
import Image from 'next/image';

// type imports
import type { CarouselImage } from '@lib/types';

// style imports
import styles from './imageModal.module.scss';

// component imports
import Icon from '@components/Icons';

/**
 * Displays a modal for full screen view of an image
 *
 * @param props - The component props object
 * @param props.image The CarouselImage to display
 * @param props.isOpen Boolean to determine if the modal is open
 * @param props.onClose Function to close the modal
 * @param props.onNext Function to go to the next image
 * @param props.onPrev Function to go to the previous image
 *
 * @returns ImageModal component
 */
const ImageModal = ({
    image,
    isOpen,
    onClose,
    onNext,
    onPrev,
}: {
    image: CarouselImage;
    isOpen: boolean;
    onClose: () => void;
    onNext: () => void;
    onPrev: () => void;
}) => {
    // Modal dialog reference
    const modalRef = useRef<HTMLDialogElement>(null);

    // Effect to open and close the modal when state changes
    useEffect(() => {
        const dialogElement = modalRef.current;
        if (!dialogElement) return;

        if (isOpen) {
            dialogElement.showModal();
        } else {
            dialogElement.close();
        }
    }, [isOpen]);

    return (
        <>
            {/* Modal dialog for full screen image view */}
            <dialog
                ref={modalRef}
                className={styles.imgModal}
                onCancel={(e) => {
                    e.preventDefault();
                    onClose();
                }}
                // Close modal when clicking outside the image
                onClick={(e) => {
                    if (e.target === modalRef.current) {
                        onClose();
                    }
                }}
                aria-modal="true"
                role="dialog"
                aria-label="Image viewer"
            >
                <Image
                    loading="lazy"
                    src={image.path}
                    width={image.width}
                    height={image.height}
                    alt={image.alt}
                    placeholder="blur"
                    blurDataURL={image.blurDataURL}
                    quality={100}
                    className={styles.img}
                />
                {/* Close button */}
                <div className={styles.closeContainer}>
                    <button
                        onClick={onClose}
                        aria-label="Close image viewer"
                        className={`${styles.closeButton} ${styles.close}`}
                    >
                        <Icon iconClass={`nav-x`} />
                    </button>
                </div>

                {/* Previous image button */}
                <button
                    className={`${styles.embla__button} ${styles.embla__button_prev}`}
                    onClick={onPrev}
                    aria-label="Previous image"
                >
                    <Icon
                        iconClass="nav-arrow-left"
                        extraClasses={[styles.embla__button__svg]}
                    />
                </button>

                {/* Next image button */}
                <button
                    className={`${styles.embla__button} ${styles.embla__button_next}`}
                    onClick={onNext}
                    aria-label="Next image"
                >
                    <Icon
                        iconClass="nav-arrow-right"
                        extraClasses={[styles.embla__button__svg]}
                    />
                </button>
            </dialog>
        </>
    );
};

export default ImageModal;
